import React from "react";
import { Link } from "react-router-dom";
import WikiContents from "./WikiContents";
import "./Wiki.css";

const ref = (id) => (
  <sup className="wiki-ref" key={id}>
    <a href={`#${id}`}>{id.replace("e", "")}</a>
  </sup>
);

const refs = (...ids) => ids.map((id) => ref(id));

const contents = [
  ["overview", "Overview"],
  ["yerkish", "Yerkish"],
  ["lexigrams", "Lexigrams"],
  ["grammar", "Correlational Grammar"],
  ["methodology", "Operational Methodology"],
  ["lana", "LANA Project"],
  ["legacy", "Legacy"],
  ["references", "References"],
];

const references = [
  {
    id: "e1",
    href: "/pdf/1978-Chimpanzee-Language-Research.pdf",
    label: "Rumbaugh & Savage-Rumbaugh, 1978",
    title: "Chimpanzee language research: Status and potential",
  },
  {
    id: "e2",
    label: "Bettoni, 2007",
    title: "The Yerkish Language: From Operational Methodology to Chimpanzee Communication",
  },
];

const WikiErnstVonGlasersfeld = () => (
  <main className="wiki-page">
    <WikiContents
      backLink={{ href: "/research", label: "Research Trajectory" }}
      sections={contents}
    />

    <article className="wiki-article">
      <h1>Ernst von Glasersfeld</h1>

      <section id="overview">
        <p className="wiki-lead">
          Ernst von Glasersfeld was a philosopher and cybernetician whose work
          shaped the technical language environment of the original{" "}
          <a href="#lana">LANA Project</a>. In the{" "}
          <a href="#yerkish">Yerkish</a> account consulted from the local wiki
          archive, he is credited with inventing Yerkish, coining the term{" "}
          <a href="#lexigrams">lexigram</a>, creating the first lexigram set,
          and designing the <a href="#grammar">grammar</a> that regulated how
          lexigrams could be combined. {refs("e2")}
        </p>
        <p>
          His importance to the ape-language research trajectory is therefore
          methodological as much as linguistic. <a href="#yerkish">Yerkish</a>{" "}
          turned the keyboard into a controlled symbolic system:{" "}
          <a href="#lana">Lana</a>'s sequences could be displayed,
          parsed, accepted or rejected, and linked to machine responses. That
          gave later researchers a way to study symbolic behavior without
          relying only on face-to-face gesture, human cueing, or ambiguous
          natural-language interpretation. {refs("e1", "e2")}
        </p>
      </section>

      <section id="yerkish">
        <h2>Yerkish</h2>
        <p>
          Yerkish was an artificial language created for computer-mediated
          communication with apes. It was not designed as a natural human
          language. Its value was operational: it gave the research a vocabulary,
          a keyboard, and a grammar simple enough for a small computer to parse
          while still rich enough for requests, naming, and sentence-like
          combinations. {refs("e1", "e2")}
        </p>
        <p>
          The point was not to make Lana speak English through a machine. The
          point was to create a symbolic field where each selection could be
          displayed, checked, responded to, and saved as data. That made Yerkish
          both a communication system and a research instrument.
          {refs("e1", "e2")}
        </p>
      </section>

      <section id="lexigrams">
        <h2>Lexigrams</h2>
        <p>
          Lexigrams were abstract word-designs placed on keyboard keys. Their
          non-representational form mattered because a lexigram was meant to
          function as a symbol, not a picture. The Yerkish paper states that von
          Glasersfeld coined the term and created the first 120 lexigrams,
          starting from words likely to matter to a young chimpanzee and the
          researchers working with her. {refs("e2")}
        </p>
        <p>
          This design choice was crucial to the later research trajectory. If a
          symbol looked like the thing it named, critics could argue that the
          ape was matching pictures. Abstract lexigrams forced the question into
          the symbolic domain: what relation had the animal learned between the
          sign, the referent, the sequence, and the social situation?
          {refs("e1", "e2")}
        </p>
      </section>

      <section id="grammar">
        <h2>Correlational Grammar</h2>
        <p>
          Von Glasersfeld's grammar regulated which lexigram sequences counted
          as acceptable Yerkish expressions. The paper describes this as a
          correlational grammar organized around correlators and correlations.
          In practice, this meant that <a href="#lana">Lana</a> was not merely pressing isolated
          keys. She was working within a system where sequence and relation were
          part of the experiment. {refs("e1", "e2")}
        </p>
      </section>

      <section id="methodology">
        <h2>Operational Methodology</h2>
        <p>
          Bettoni's account connects Yerkish to operational methodology,
          especially the idea that thought can be studied through operations of
          correlation. In that frame, Yerkish was not simply a vocabulary list.
          It was an attempt to make relational structure visible enough for a
          machine and a researcher to evaluate.
          {refs("e2")}
        </p>
      </section>

      <section id="lana">
        <h2>LANA Project</h2>
        <p>
          The LANA Project used von Glasersfeld's language design inside a
          computer-controlled environment at Yerkes. <a href="#overview">Lana</a> could press keys,
          produce visible lexigram sequences, and receive machine-mediated
          responses. The 1978 status paper treats this project as the beginning
          of the research program that later expanded to Sherman, Austin, and
          additional questions about reference, naming, and communication.
          {refs("e1")}
        </p>
      </section>

      <section id="legacy">
        <h2>Legacy</h2>
        <p>
          Von Glasersfeld's importance is easiest to miss because later public
          attention often focuses on the apes themselves. But the research
          trajectory depends on the system he helped make possible: abstract
          lexigrams, a formal grammar, computer-mediated display, and an
          environment where symbolic acts could be treated as evidence.
          {refs("e1", "e2")}
        </p>
      </section>

      <section id="references">
        <div className="wiki-reference-head">
          <h2>References</h2>
          <Link to="/research">Back to trajectory</Link>
        </div>
        <ol className="wiki-references">
          {references.map((source) => (
            <li id={source.id} key={source.id}>
              {source.href ? (
                <a href={source.href} target="_blank" rel="noreferrer">
                  {source.label}. <em>{source.title}</em>.
                </a>
              ) : (
                <>
                  {source.label}. <em>{source.title}</em>.
                </>
              )}
            </li>
          ))}
        </ol>
      </section>
    </article>
  </main>
);

export default WikiErnstVonGlasersfeld;
